import type {AssembleTilesRequest, AssembleTilesResponse} from "@cm/render-nodes"
import {assignAtlasSlots, buildTileIndexMap, getAtlasSlotSize, tileKey, TRACE_TILE_ASSEMBLY} from "@cm/render-nodes"
import type {AbstractTileLoader, TileRequest} from "@cm/three-texture-streaming"
import {TileFetchParamsHelper} from "@cm/three-texture-streaming"
import {encodeUncompressedTiff} from "@cm/utils"

// TEMP DEBUG: switch to compare the two assembled-atlas encodings while investigating interactive-preview
// tile assembly perf. true = uncompressed TIFF (near-memcpy encode); false = lossy JPEG (slower encode, much
// smaller payload). Defaults to TIFF now that the atlas is bounded by requested-tile count rather than full
// texture resolution, making the encode-time/payload-size tradeoff far less punishing either way - kept
// around as a toggle for further comparison.
const USE_UNCOMPRESSED_TIFF = true
// Source tiles are themselves already JPEG-encoded (alpha stripped, lossy) upstream of this code, for
// every texture type including raw/data maps (normal, roughness, etc.) - so re-encoding the assembled
// result as JPEG adds no meaningful additional loss beyond what's already baked into the tiles. A high
// quality setting keeps this re-encode close to lossless while staying fast, unlike PNG's deflate step.
const JPEG_QUALITY = 0.95

// Drops every 4th (alpha) byte from an RGBA buffer. Simple, branchless, sequential copy - negligible
// next to what JPEG-encoding the same buffer used to cost.
function stripAlphaChannel(rgba: Uint8ClampedArray): Uint8Array {
    const pixelCount = rgba.length / 4
    const rgb = new Uint8Array(pixelCount * 3)
    for (let src = 0, dst = 0; dst < rgb.length; src += 4, dst += 3) {
        rgb[dst] = rgba[src]
        rgb[dst + 1] = rgba[src + 1]
        rgb[dst + 2] = rgba[src + 2]
    }
    return rgb
}

// Extracts a single (red) channel from an RGBA buffer - used for tileChannels===1 (grayscale) textures,
// whose source tiles decode to R=G=B in-browser regardless.
function extractSingleChannel(rgba: Uint8ClampedArray): Uint8Array {
    const pixelCount = rgba.length / 4
    const out = new Uint8Array(pixelCount)
    for (let src = 0, dst = 0; dst < out.length; src += 4, dst++) {
        out[dst] = rgba[src]
    }
    return out
}

export async function assembleTiles<TResponseMap extends {ImageBitmap: ImageBitmap}>(
    textureTileLoader: AbstractTileLoader<TileRequest, TResponseMap>,
    request: AssembleTilesRequest,
): Promise<AssembleTilesResponse> {
    const atlasLayout = assignAtlasSlots(request.imageTileIndices)
    const slotSize = getAtlasSlotSize(request.imageTilesDescriptor)

    const tileFetchParamsHelper = new TileFetchParamsHelper(
        request.imageTilesDescriptor,
        {
            width: request.imageTilesDescriptor.tileWidth,
            height: request.imageTilesDescriptor.tileHeight,
        },
        request.imageTilesDescriptor.tileMargin,
    )
    const downloadStart = performance.now()
    const imageTiles = await Promise.all(
        request.imageTileIndices.map(async (tileIndex) => {
            const tileFetchParams = tileFetchParamsHelper.getTileFetchParams(tileIndex.mipLevel, tileIndex.tileIndex)
            // priority/abortSignal: assembleTiles has no priority or cancellation concept of its own
            // today (the main-thread fetch this replaces had neither either). A fixed low priority means
            // these fetches yield to the live three.js preview's own tile requests on the shared loader's
            // queue when it's contended.
            return textureTileLoader.requestTile({...tileFetchParams, format: "rgba8"}, "ImageBitmap", 0, new AbortController().signal)
        }),
    )
    const tileDownloadTimeInMs = performance.now() - downloadStart

    const compositeStart = performance.now()
    const atlasWidth = atlasLayout.cols * slotSize.width
    const atlasHeight = atlasLayout.rows * slotSize.height
    const offscreenCanvas = new OffscreenCanvas(atlasWidth, atlasHeight)
    // willReadFrequently: the TIFF path reads the whole canvas back via getImageData below, so keep it
    // CPU-backed rather than paying for a GPU readback of the surface.
    const ctx = offscreenCanvas.getContext("2d", {willReadFrequently: USE_UNCOMPRESSED_TIFF})
    if (!ctx) {
        throw Error("Failed to create 2D context for offscreen canvas")
    }
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = "high"
    // Every downloaded tile is already exactly one slot's pixel size (margin included), regardless of mip
    // level - no per-tile scaling needed, unlike the old flat-canvas layout where coarser tiles were drawn
    // proportionally larger. Atlas slots never overlap each other (unlike finest-grid coverage, which can),
    // so draw order doesn't matter here.
    request.imageTileIndices.forEach((tileInfo, index) => {
        const slot = atlasLayout.slotByTileKey.get(tileKey(tileInfo))
        if (!slot) {
            return // should not happen: every requested tile was assigned a slot by assignAtlasSlots
        }
        ctx.drawImage(imageTiles[index], slot.slotX * slotSize.width, slot.slotY * slotSize.height)
    })
    const compositeTimeInMs = performance.now() - compositeStart

    const encodeStart = performance.now()
    const hasAlpha = request.imageTilesDescriptor.tileChannels === 4 || request.imageTilesDescriptor.tileChannels === 2
    const isSingleChannel = request.imageTilesDescriptor.tileChannels === 1
    const atlasChannels: 1 | 3 | 4 = hasAlpha ? 4 : isSingleChannel ? 1 : 3
    let atlasMediaType: string
    let atlasEncodedData: Uint8Array
    if (USE_UNCOMPRESSED_TIFF) {
        atlasMediaType = "image/tiff"
        const imageData = ctx.getImageData(0, 0, atlasWidth, atlasHeight)
        const pixels = hasAlpha ? imageData.data : isSingleChannel ? extractSingleChannel(imageData.data) : stripAlphaChannel(imageData.data)
        atlasEncodedData = encodeUncompressedTiff(pixels, atlasWidth, atlasHeight, atlasChannels)
    } else {
        atlasMediaType = "image/jpeg"
        const blob = await offscreenCanvas.convertToBlob({type: atlasMediaType, quality: JPEG_QUALITY})
        atlasEncodedData = new Uint8Array(await blob.arrayBuffer())
    }

    // Tile index map: always lossless TIFF, it stores exact packed integers (atlas slot + mip level), not visual data, so it can never be lossy.
    const indexMap = buildTileIndexMap({
        descriptor: request.imageTilesDescriptor,
        requestedTiles: request.imageTileIndices,
        atlasLayout,
    })
    const indexMapEncodedData = encodeUncompressedTiff(indexMap.data, indexMap.width, indexMap.height, 3)

    const encodeTimeInMs = performance.now() - encodeStart
    const tileAssemblyTimeInMs = compositeTimeInMs + encodeTimeInMs
    if (TRACE_TILE_ASSEMBLY) {
        // eslint-disable-next-line no-console
        console.log(
            `[tile-assembly] dataObject=${request.dataObjectId} tiles=${request.imageTileIndices.length} atlas=${atlasWidth}x${atlasHeight}` +
                ` indexMap=${indexMap.width}x${indexMap.height} download=${tileDownloadTimeInMs.toFixed(1)}ms composite=${compositeTimeInMs.toFixed(1)}ms encode=${encodeTimeInMs.toFixed(1)}ms`,
        )
    }
    return {
        atlas: {
            encodedData: atlasEncodedData,
            mediaType: atlasMediaType,
        },
        indexMap: {
            encodedData: indexMapEncodedData,
            mediaType: "image/tiff",
        },
        tileDownloadTimeInMs,
        tileAssemblyTimeInMs,
        compositeTimeInMs,
        encodeTimeInMs,
    }
}
